import { VacancyResponse } from "@/lib/Types/VacancyResponse/VacancyResponse";
import { SpecialDealDetail } from "@/lib/Types/SpecialDealDetail";
import { FlexiOfferData } from "@/lib/Types/FlexiOfferData";
import { ViewModeConfig } from "@/lib/Helpers/currentUrl";

export interface BuildBookingUrlParams {
  // From vacancy response
  vacancyResponse: VacancyResponse;

  // From offer data
  hotelCid: string;
  objectCode: string;
  hasFlexibleOffer: boolean;
  flexibleOfferData?: FlexiOfferData | null;
  specialDealsDetails?: SpecialDealDetail[] | null;
  estimatedTotalPriceRrp?: number;
  isUpsellingOffer?: boolean;

  // From current URL search params
  searchParams: URLSearchParams;

  // From tracking
  selectionId?: string | null;
  clickId?: string;

  // Price curve data (optional)
  priceCurveDate?: string | null;
  isInitialDateRange?: boolean;

  // ViewMode config
  viewMode: ViewModeConfig;
}

export function buildBookingUrl({
  vacancyResponse,
  hotelCid,
  objectCode,
  hasFlexibleOffer,
  flexibleOfferData,
  specialDealsDetails,
  estimatedTotalPriceRrp,
  isUpsellingOffer,
  searchParams,
  selectionId,
  clickId,
  priceCurveDate,
  isInitialDateRange = false,
  viewMode,
}: BuildBookingUrlParams): string {

  // Extract special deal rule IDs
  const bookOnOptionDeal = specialDealsDetails?.find(
    (deal) => deal.specialDealTypes === "BOOK_ON_OPTION"
  );
  const toVoucherDeal = specialDealsDetails?.find(
    (deal) => deal.specialDealTypes === "TO_VOUCHER"
  );

  // Build the booking JSON object
  const bookingObject = {
    cid: hotelCid,
    travellers: searchParams.get("rooms")?.split(";").join(",") ?? "",
    rooms: searchParams.get("rooms") ?? "",
    tourOperatorCode: vacancyResponse.tourOperator?.code ?? "",
    toVoucherCodeRuleId: toVoucherDeal?.ruleId,
    departureDate: vacancyResponse.departureDate,
    returnDate: vacancyResponse.returnDate,
    departureAirport:
      vacancyResponse.flights?.outwardFlight?.[0]?.departure?.airport ?? "",
    // Last segment: for stopover flights, [0].destination is the layover
    // airport, not the final destination (MKTP — checkout vacancy2 mismatch)
    destinationAirport:
      vacancyResponse.flights?.outwardFlight?.at(-1)?.destination?.airport ??
      "",
    price: parseFloat(vacancyResponse.travelPriceSum) || 0,
    softBooking: vacancyResponse.softBooking,
    bookingInfo: vacancyResponse.bookingInfo
      ? {
          offerID: vacancyResponse.bookingInfo.offerID,
          sessionId: vacancyResponse.bookingInfo.sessionId,
          bookingType: vacancyResponse.bookingInfo.bookingType ?? "BOOKONFIX",
          sessionTime: vacancyResponse.bookingInfo.sessionTime,
          mustCheck: vacancyResponse.bookingInfo.mustCheck,
          specialHotel: vacancyResponse.bookingInfo.specialHotel ?? false,
          reservationId: vacancyResponse.bookingInfo.reservationId,
        }
      : undefined,
    p: viewMode.profile,
    m: viewMode.mandator,
    channel: viewMode.channel,
    priceCurveOffer: priceCurveDate ?? false,
    initialDateRange: isInitialDateRange,
  };

  // Build URL params - start with current search params (filters, etc.)
  const urlParams = new URLSearchParams();

  // Pass through all current search params (filters like mealType, categories, etc.)
  searchParams.forEach((value, key) => {
    urlParams.set(key, value);
  });

  // Override clickId with fresh value (searchParams may have a stale one
  // because useReactiveSearchParams doesn't observe silent replaceState updates)
  if (clickId !== undefined) {
    if (clickId) {
      urlParams.set("clickId", clickId);
    } else {
      urlParams.delete("clickId");
    }
  }

  // Add/override booking-specific params
  urlParams.set("booking", JSON.stringify(bookingObject));
  urlParams.set("objectcode", objectCode);
  urlParams.set("transactionId", vacancyResponse.transactionId);
  urlParams.set("hasFlexibleOffer", String(hasFlexibleOffer));

  // Add travellers as separate param (same format as rooms but with semicolons replaced by commas)
  const travellers = searchParams.get("rooms")?.split(";").join(",") ?? "";
  if (travellers) {
    urlParams.set("travellers", travellers);
  }

  if (hasFlexibleOffer) {
    urlParams.set("flexibleOffers", "flexibleCancellation");
  }

  if (flexibleOfferData?.ruleId) {
    urlParams.set("ruleId", flexibleOfferData.ruleId);
  }

  if (bookOnOptionDeal?.ruleId) {
    urlParams.set("bookOnOptionRuleId", String(bookOnOptionDeal.ruleId));
  }

  if (selectionId) {
    urlParams.set("selectionid", selectionId);
  }

  // Only set rrp if it's a valid number (not null/undefined)
  if (estimatedTotalPriceRrp != null) {
    urlParams.set("rrp", String(estimatedTotalPriceRrp));
  }

  if (isUpsellingOffer) {
    urlParams.set("isUpsellingOffer", "true");
  }

  return `/buchung?${urlParams.toString()}`;
}
